// src/pages/motolivraison/commerce-detail.page.jsx
//
// Détail d'un commerce avec toutes ses informations + actions admin.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Statistic, Button, message, Space,
  Breadcrumb, Avatar, Spin
} from 'antd';
import {
  ShopOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const CommerceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commerce, setCommerce] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommerceById(id);
      setCommerce(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleAction = async (action) => {
    try {
      const labels = { validate: 'approuvé', suspend: 'suspendu', reactivate: 'réactivé' };
      if (action === 'validate') await motolivraisonService.validateCommerce(id);
      else if (action === 'suspend') await motolivraisonService.suspendCommerce(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommerce(id);
      message.success(`Commerce ${labels[action]}`);
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !commerce) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red', rejected: 'default' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu', rejected: 'Rejeté' })[s] || s;

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commerces">Commerces</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{commerce.nom}</Breadcrumb.Item>
        </Breadcrumb>

        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
          Retour
        </Button>

        <Card>
          <Row gutter={24}>
            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
              <Avatar size={120} src={commerce.logo} icon={<ShopOutlined />} style={{ backgroundColor: '#fa8c16' }} />
              <h2 style={{ marginTop: 16 }}>{commerce.nom}</h2>
              <Tag color={statusColor(commerce.status)} style={{ fontSize: 13, padding: '4px 12px' }}>
                {statusLabel(commerce.status)}
              </Tag>
            </Col>

            <Col xs={24} md={18}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Catégorie" span={2}>
                  {commerce.categorieId?.nom || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Adresse" span={2}>{commerce.adresse}</Descriptions.Item>
                {commerce.quartier && (
                  <Descriptions.Item label="Quartier">{commerce.quartier}</Descriptions.Item>
                )}
                <Descriptions.Item label="Ville">{commerce.cityId?.name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Téléphone">{commerce.telephone}</Descriptions.Item>
                {commerce.email && (
                  <Descriptions.Item label="Email">{commerce.email}</Descriptions.Item>
                )}
                {commerce.description && (
                  <Descriptions.Item label="Description" span={2}>{commerce.description}</Descriptions.Item>
                )}
              </Descriptions>

              {commerce.ownerId && (
                <Card size="small" title="Propriétaire" style={{ marginTop: 16 }}>
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Nom">
                      {commerce.ownerId.firstName} {commerce.ownerId.lastName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Téléphone">{commerce.ownerId.phone}</Descriptions.Item>
                    {commerce.ownerId.email && (
                      <Descriptions.Item label="Email" span={2}>{commerce.ownerId.email}</Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              )}
            </Col>
          </Row>
        </Card>

        <Card title="Statistiques" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={6}><Statistic title="Articles" value={commerce.stats?.totalArticles || 0} /></Col>
            <Col xs={6}><Statistic title="Articles actifs" value={commerce.stats?.articlesActifs || 0} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={6}><Statistic title="Commandes" value={commerce.stats?.totalCommandes || 0} /></Col>
            <Col xs={6}><Statistic title="Revenus brut" value={commerce.stats?.revenusBrut || 0} suffix=" F" /></Col>
          </Row>
        </Card>

        <Card title="Actions" style={{ marginTop: 16 }}>
          <Space wrap>
            {commerce.status === 'pending' && (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAction('validate')}>
                Approuver
              </Button>
            )}
            {commerce.status === 'approved' && (
              <Button danger icon={<StopOutlined />} onClick={() => handleAction('suspend')}>
                Suspendre
              </Button>
            )}
            {commerce.status === 'suspended' && (
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleAction('reactivate')}>
                Réactiver
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default CommerceDetail;
